import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-slate-900 text-white p-12 flex-col justify-between">
        <Link to="/" className="font-display text-2xl font-black tracking-tighter" data-testid="brand-link">
          Stay<span className="text-blue-500">Care</span>
        </Link>
        <div>
          <h2 className="font-display text-5xl font-black tracking-tighter leading-none">
            Welcome back.
          </h2>
          <p className="mt-4 text-slate-400 max-w-md">
            Pick up where you left off — your tenants, rents and reports are waiting.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">© StayCare</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-slate-50">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter">Sign in</h1>
            <p className="text-slate-500 mt-1 text-sm">
              No account? <Link className="text-blue-600 hover:underline" to="/register" data-testid="register-link">Create one</Link>
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Email or username</label>
            <input
              data-testid="login-email"
              type="text"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-slate-300 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="you@example.com or username"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 block mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 px-4 py-3 pr-11 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="••••••"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                      data-testid="login-toggle-password"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-slate-700">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-blue-600 text-white py-3.5 font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
